import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import api from "../../../../chat/api";

/**
 * Coerce the edit form's raw value into the payload shape the backend
 * expects for the given field type. The bound input keeps the raw value
 * as a string (or boolean for checkboxes), so this function normalizes
 * before PATCH.
 */
const buildAnswerPayload = (field, raw) => {
  if (raw === null || raw === undefined || raw === "") return null;
  const ftype = (field?.field_type || "text").toLowerCase();
  if (ftype === "checkbox") {
    if (typeof raw === "boolean") return raw;
    if (raw === "true") return true;
    if (raw === "false") return false;
    return null;
  }
  return String(raw);
};

/**
 * Owns the inline field editor's UI state + the PATCH that saves an
 * answer override. Mutates the parent `job` via `setJob` so the bbox
 * overlay re-renders with the new answer immediately, and updates the
 * "any field has answers?" derived state via `setHasAnswers`.
 */
export default function useFieldEditor({ jobId, job, setJob, setHasAnswers }) {
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const openFieldEditor = useCallback(
    (field) => {
      if (savingEdit) return;
      setEditingField(field);
      setEditValue(field?.answer ?? "");
    },
    [savingEdit]
  );

  const closeFieldEditor = useCallback(() => {
    if (savingEdit) return;
    setEditingField(null);
    setEditValue("");
  }, [savingEdit]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingField || savingEdit) return;
    setSavingEdit(true);
    try {
      const payload = { answer: buildAnswerPayload(editingField, editValue) };
      const res = await api.patch(
        `/api/v1/rfp/autopdf/jobs/${jobId}/fields/${editingField.id}/`,
        payload
      );
      if (![200, 201].includes(res.status)) {
        throw new Error("Patch failed");
      }
      const updated = res.data || {};

      // Merge the updated field back into the job state so the bbox
      // overlay re-renders with the new answer immediately.
      setJob((prev) => {
        if (!prev) return prev;
        const fields = (prev.fields || []).map((f) =>
          f.id === editingField.id
            ? {
                ...f,
                answer: updated.answer ?? payload.answer ?? null,
                user_overridden: updated.user_overridden ?? true,
                confidence: 1.0,
                needs_review: false,
              }
            : f
        );
        return { ...prev, fields };
      });

      // hasAnswers may flip based on the new edit.
      const stillHasAnswers = (job?.fields || [])
        .map((f) =>
          f.id === editingField.id ? { ...f, answer: payload.answer } : f
        )
        .some((f) => f.answer && f.answer.toString().trim() !== "");
      setHasAnswers(stillHasAnswers);

      toast.success("Field updated.");
      setEditingField(null);
      setEditValue("");
    } catch (err) {
      console.error("Field update failed:", err);
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to update field.";
      toast.error(msg);
    } finally {
      setSavingEdit(false);
    }
  }, [editingField, editValue, savingEdit, jobId, job, setJob, setHasAnswers]);

  return {
    editingField,
    editValue,
    setEditValue,
    savingEdit,
    openFieldEditor,
    closeFieldEditor,
    handleSaveEdit,
    // Exposed so the delete hook can clear the editor if the deleted
    // field is currently open.
    setEditingField,
  };
}
